<script setup lang="ts">
import type { THeading } from '~/components/ui/heading'

const props = defineProps<{
  position: 'upper' | 'content' | 'bottom'
  title?: string
  headingLevel?: THeading
  /** Visual size of the title when it differs from its level, e.g. h3 for an h2 */
  headingClass?: string
  leadingLevel?: string
  description?: string
  variables?: Record<string, string | number>
}>()

const route = useRoute()

// Try to use existing SEO data from app.vue first, fallback to cache system
const nuxtApp = useNuxtApp()
const existingSeoData = computed(() => nuxtApp._seoData?.value)

// Only load cache data if no existing SEO data is available
const { data: cachedData } = existingSeoData.value
  ? { data: ref({ data: { data: {} } }) }
  : await useAppCache()

const seoData = computed(() => {
  // Use existing SEO data if available
  if (existingSeoData.value) {
    return existingSeoData.value
  }

  // Fallback to cache system
  let currentPath = route.path.startsWith('/') ? route.path.substring(1) : route.path

  if (currentPath.startsWith('ky/')) {
    currentPath = currentPath.substring(3)
  }

  const seoEntries = cachedData.value?.data?.data || {}

  if (seoEntries[currentPath]) return seoEntries[currentPath]

  for (const key in seoEntries) {
    if (key.includes('(')) {
      const regex = new RegExp(`^${key}$`)
      if (regex.test(currentPath)) {
        return seoEntries[key]
      }
    }
  }

  return null
})

const replaceVariables = (text: string | null) => {
  if (!text) return ''

  return text.replace(/:([a-zA-Z0-9_.]+):/g, (_, key) => {
    return props.variables?.[key] !== undefined ? String(props.variables[key]) : ''
  })
}

const currentBlock = computed(() => {
  let title = props.title ? replaceVariables(props.title) : ''
  let content = props.description ? replaceVariables(props.description) : ''

  if (!seoData.value || (seoData.value.type && seoData.value.type !== 'seo_settings')) {
    return props.position === 'upper' ? { title, content } : null
  }

  if (props.position === 'upper') {
    title = replaceVariables(seoData.value.title)
    content = replaceVariables(seoData.value.upper_description)
    return { title, content }
  }

  if (props.position === 'content') {
    return replaceVariables(seoData.value.content)
  }

  if (props.position === 'bottom') {
    return replaceVariables(seoData.value.bottom_description)
  }

  return null
})

const computedLeading = computed(() => {
  return props.leadingLevel ?? 10
})
</script>

<template>
  <div
    v-if="
      (typeof currentBlock === 'string' && currentBlock.trim().length > 0) ||
      (typeof currentBlock === 'object' &&
        currentBlock !== null &&
        Object.values(currentBlock).some((v) => !!v))
    "
  >
    <template v-if="props.position === 'upper' && typeof currentBlock === 'object'">
      <ContentBlock variant="white">
        <Heading
          v-if="currentBlock.title"
          :tag="headingLevel ?? 'h1'"
          :class="[`leading-${computedLeading}`, props.headingClass]"
        >
          {{ currentBlock.title }}
        </Heading>
        <Content v-if="currentBlock.content && currentBlock.content.length > 0">
          <div v-hydrated-html="currentBlock.content" class="mx-auto max-w-none space-y-5" />
        </Content>
      </ContentBlock>
    </template>
    <template v-else>
      <ContentBlock variant="white">
        <Content>
          <div v-hydrated-html="currentBlock" class="mx-auto max-w-none space-y-5" />
        </Content>
      </ContentBlock>
    </template>
  </div>
</template>
